import { useState, useCallback, useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Tag,
  Plus,
  MoreHorizontal,
  Edit,
  Trash2,
  Loader2,
  RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { usePermissions } from "@/hooks/usePermissions";
import { platformApi, type DocumentLabel } from "@/services/api/platform";

export const Route = createFileRoute("/portal/knowledgebase-file/labels")({
  component: LabelsPage,
});

function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_]+/g, "-")
    .replace(/-+/g, "-");
}

const PRESET_COLORS = [
  "#ef4444", "#f97316", "#eab308", "#22c55e", "#06b6d4",
  "#3b82f6", "#8b5cf6", "#ec4899", "#6b7280",
];

function LabelsPage() {
  return <LabelsPageContent />;
}

function LabelsPageContent() {
  const { token } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const { can, isAdmin } = usePermissions();
  const canCreateLabels = can("create", "kb-file");
  const canEditLabels = can("edit", "kb-file");
  const canDeleteLabels = isAdmin;
  const [loading, setLoading] = useState(true);
  const [labels, setLabels] = useState<DocumentLabel[]>([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [newLabel, setNewLabel] = useState({ name: "", description: "", color: "" });
  const [editLabel, setEditLabel] = useState<DocumentLabel | null>(null);
  const [labelToDelete, setLabelToDelete] = useState<DocumentLabel | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const organisationId = selectedOrganisation?.organisationId;

  const loadLabels = useCallback(async () => {
    if (!token || !organisationId) return;
    setLoading(true);
    try {
      const result = await platformApi.listLabels(token, {
        organisation_id: organisationId,
      });
      setLabels(result);
    } catch (error) {
      console.error("Failed to load labels:", error);
      toast.error("Failed to load labels");
    } finally {
      setLoading(false);
    }
  }, [token, organisationId]);

  useEffect(() => {
    if (token && organisationId) {
      loadLabels();
    }
  }, [token, organisationId, loadLabels]);

  const handleCreateLabel = async () => {
    if (!token || !organisationId) return;
    if (!newLabel.name.trim()) {
      toast.error("Label name is required");
      return;
    }
    setSaving(true);
    try {
      await platformApi.createLabel(token, {
        name: newLabel.name,
        slug: slugify(newLabel.name),
        organisation_id: organisationId,
        description: newLabel.description || undefined,
        color: newLabel.color || undefined,
      });
      toast.success("Label created");
      setCreateDialogOpen(false);
      setNewLabel({ name: "", description: "", color: "" });
      loadLabels();
    } catch (error) {
      console.error("Failed to create label:", error);
      toast.error("Failed to create label");
    } finally {
      setSaving(false);
    }
  };

  const handleEditLabel = async () => {
    if (!token || !organisationId || !editLabel) return;
    if (!editLabel.name.trim()) {
      toast.error("Label name is required");
      return;
    }
    setSaving(true);
    try {
      await platformApi.updateLabel(token, editLabel.id, organisationId, {
        name: editLabel.name,
        description: editLabel.description ?? undefined,
        color: editLabel.color ?? undefined,
      });
      toast.success("Label updated");
      setEditDialogOpen(false);
      setEditLabel(null);
      loadLabels();
    } catch (error) {
      console.error("Failed to update label:", error);
      toast.error("Failed to update label");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLabel = async () => {
    if (!token || !organisationId || !labelToDelete) return;
    setDeleting(true);
    try {
      await platformApi.deleteLabel(token, labelToDelete.id, organisationId);
      toast.success("Label deleted");
      setDeleteDialogOpen(false);
      setLabelToDelete(null);
      loadLabels();
    } catch (error) {
      console.error("Failed to delete label:", error);
      toast.error("Failed to delete label");
    } finally {
      setDeleting(false);
    }
  };

  if (loading && labels.length === 0) {
    return (
      <div className="container mx-auto py-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Tag className="h-6 w-6" />
            Labels
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Create and manage document labels for{" "}
            {selectedOrganisation?.organisationName || "your organisation"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadLabels}
            disabled={loading}
          >
            <RefreshCw
              className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
          {canCreateLabels && (
            <Button size="sm" onClick={() => setCreateDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Label
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Labels</CardTitle>
            <Tag className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{labels.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Assignments
            </CardTitle>
            <Tag className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {labels.reduce((sum, l) => sum + l.usage_count, 0)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Label List */}
      <Card>
        <CardHeader className="py-4">
          <CardTitle className="text-base">All Labels</CardTitle>
          <CardDescription className="text-xs">
            Labels can be assigned to documents for filtering queries.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          {labels.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Tag className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No labels yet</p>
              <p className="text-xs mt-2">
                Create a label to start tagging your documents
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              {labels.map((label) => (
                <div
                  key={label.id}
                  className="flex items-center gap-3 p-3 rounded-md hover:bg-muted/50 group"
                >
                  {label.color ? (
                    <div
                      className="w-4 h-4 rounded-full flex-shrink-0"
                      style={{ backgroundColor: label.color }}
                    />
                  ) : (
                    <Tag className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{label.name}</div>
                    {label.description && (
                      <div className="text-xs text-muted-foreground truncate">
                        {label.description}
                      </div>
                    )}
                  </div>
                  <Badge variant="outline" className="text-xs flex-shrink-0">
                    {label.usage_count} doc{label.usage_count !== 1 ? "s" : ""}
                  </Badge>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 flex-shrink-0 opacity-0 group-hover:opacity-100"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {canEditLabels && (
                        <DropdownMenuItem
                          onClick={() => {
                            setEditLabel({ ...label });
                            setEditDialogOpen(true);
                          }}
                        >
                          <Edit className="mr-2 h-4 w-4" />
                          Edit
                        </DropdownMenuItem>
                      )}
                      {canDeleteLabels && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={() => {
                              setLabelToDelete(label);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Label Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Label</DialogTitle>
            <DialogDescription>Add a new document label.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Label Name</Label>
              <Input
                placeholder="e.g., Compliance, Internal, Draft"
                value={newLabel.name}
                onChange={(e) =>
                  setNewLabel({ ...newLabel, name: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Brief description of this label..."
                value={newLabel.description}
                onChange={(e) =>
                  setNewLabel({ ...newLabel, description: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex items-center gap-2 flex-wrap">
                {PRESET_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`w-7 h-7 rounded-full border-2 transition-all ${
                      newLabel.color === color
                        ? "border-foreground scale-110"
                        : "border-transparent hover:border-muted-foreground/50"
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() =>
                      setNewLabel({
                        ...newLabel,
                        color: newLabel.color === color ? "" : color,
                      })
                    }
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleCreateLabel} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create Label"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Label Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Label</DialogTitle>
            <DialogDescription>Update the label details.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Label Name</Label>
              <Input
                value={editLabel?.name || ""}
                onChange={(e) =>
                  setEditLabel((prev) =>
                    prev ? { ...prev, name: e.target.value } : null,
                  )
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={editLabel?.description || ""}
                onChange={(e) =>
                  setEditLabel((prev) =>
                    prev ? { ...prev, description: e.target.value } : null,
                  )
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex items-center gap-2 flex-wrap">
                {PRESET_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`w-7 h-7 rounded-full border-2 transition-all ${
                      editLabel?.color === color
                        ? "border-foreground scale-110"
                        : "border-transparent hover:border-muted-foreground/50"
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() =>
                      setEditLabel((prev) =>
                        prev
                          ? {
                              ...prev,
                              color: prev.color === color ? null : color,
                            }
                          : null,
                      )
                    }
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditLabel} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Label</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{labelToDelete?.name}
              &quot;? This action cannot be undone.
              {labelToDelete && labelToDelete.usage_count > 0 && (
                <span className="block mt-2 text-amber-600">
                  Warning: This label is assigned to{" "}
                  {labelToDelete.usage_count} document
                  {labelToDelete.usage_count !== 1 ? "s" : ""}.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
              onClick={handleDeleteLabel}
              disabled={deleting}
            >
              {deleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
